import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FaPaperclip, FaDownload } from 'react-icons/fa';
import LazyImageLightbox from 'components/common/LazyImageLightbox';
import { formatFileSize, getFileIcon } from 'helpers/utils';

const IMAGE_EXT = /\.(jpe?g|png|gif|webp)$/i;

const normalize = (attachment, idx) => ({
  key: attachment.id || `attachment-${idx}`,
  name: attachment.file_name || attachment.name || `Attachment ${idx + 1}`,
  url: attachment.url || attachment.file_url || attachment.path,
  size: attachment.file_size || attachment.size,
  contentType: attachment.content_type || '',
});

const isImage = (file) => Boolean(file.url) && (file.contentType.startsWith('image/') || IMAGE_EXT.test(file.name));

// File đính kèm của một response. Ảnh hiện thành thumbnail nhỏ, bấm để xem cỡ
// lớn trong lightbox; các file khác giữ dạng chip tải về. Trước đây ảnh đính kèm
// cũng chỉ là chip tên file — phải tải về máy mới biết là ảnh gì.
const ResponseAttachments = ({ documents }) => {
  const [gallery, setGallery] = useState(null);

  const { images, files } = useMemo(() => {
    const all = (documents || []).filter(Boolean).map(normalize);
    return { images: all.filter(isImage), files: all.filter((file) => !isImage(file)) };
  }, [documents]);

  const openImage = useCallback((index) => {
    setGallery({ slides: images.map((img) => ({ src: img.url, alt: img.name })), index });
  }, [images]);

  const closeGallery = useCallback(() => setGallery(null), []);

  if (images.length === 0 && files.length === 0) return null;

  return (
    <div className="tw-mt-2 tw-space-y-1.5">
      {images.length > 0 && (
        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
          {images.map((img, index) => (
            <button
              key={img.key}
              type="button"
              title={img.name}
              aria-label={`View image ${img.name}`}
              onClick={() => openImage(index)}
              className="tw-p-0 tw-border-0 tw-bg-slate-50 tw-rounded-md tw-overflow-hidden tw-ring-1 tw-ring-slate-200 hover:tw-ring-primary-300 tw-cursor-zoom-in tw-transition-shadow hover:tw-shadow-md"
            >
              <img
                src={img.url}
                alt={img.name}
                loading="lazy"
                decoding="async"
                className="tw-block tw-h-16 tw-w-24 tw-object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5">
          <FaPaperclip size={10} className="tw-text-slate-400" />
          {files.map((file) => {
            const FileIcon = getFileIcon(file.name);
            return (
              <a
                key={file.key}
                href={file.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                download
                title={file.name}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-max-w-[220px] tw-px-2 tw-py-1 tw-rounded-md tw-bg-slate-50 tw-ring-1 tw-ring-slate-200 tw-text-slate-600 tw-no-underline hover:tw-bg-slate-100 hover:tw-text-slate-800 tw-transition-colors"
              >
                <FileIcon className="tw-text-primary-600 tw-shrink-0" size={12} />
                <span className="tw-text-[11px] tw-truncate">{file.name}</span>
                {file.size && (
                  <span className="tw-text-[10px] tw-text-slate-400 tw-shrink-0">{formatFileSize(file.size)}</span>
                )}
                <FaDownload className="tw-text-slate-400 tw-shrink-0" size={9} />
              </a>
            );
          })}
        </div>
      )}

      <LazyImageLightbox gallery={gallery} onClose={closeGallery} />
    </div>
  );
};

ResponseAttachments.propTypes = {
  documents: PropTypes.arrayOf(PropTypes.object),
};

ResponseAttachments.defaultProps = { documents: [] };

export default ResponseAttachments;
